// /marketing/utm — UTM Builder + Convention reference + history.
//
// Replaces the planned "BWM UTM Convention v1" Google Sheet entirely.
// The convention values (allowed source/medium) live in code; campaign +
// content slugs are validated against kebab-case at entry. Every link
// generated is saved to utm_links so we have a history.
//
// Pick-from-existing-post dropdown auto-fills utm_campaign from a
// content_log slug — prevents typo'd campaign values that wouldn't
// match the related post's UTM column.

import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../utils/apiFetch';
import { useContent } from '../../context/ContentContext';
import {
  UTM_SOURCES,
  UTM_MEDIUMS,
  UTM_TERMS,
  isValidKebab,
  toKebab,
  buildUtmUrl,
} from './utmConvention';

const DEFAULT_BASE_URL = 'https://bullogicwealth.com/articles/';
const inputClass = 'w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue';
const labelClass = 'block text-[10px] font-bold uppercase tracking-wider text-navy mb-1';

export default function UTMBuilderView() {
  const { entries: posts } = useContent();

  const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE_URL);
  const [utmSource, setUtmSource] = useState('blog');
  const [utmMedium, setUtmMedium] = useState('content');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [contentLogId, setContentLogId] = useState('');
  const [notes, setNotes] = useState('');

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(null);

  // Load history on mount
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/data/utm')
      .then(r => r.json())
      .then(d => { if (!cancelled) { setHistory(d.utm || []); setLoadingHistory(false); } })
      .catch(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
  }, []);

  // Pick-from-existing-post: auto-fill utm_campaign from the post slug
  function handlePickPost(id) {
    setContentLogId(id);
    if (!id) return;
    const post = posts.find(p => String(p.id) === String(id));
    if (post) {
      const slug = post.slug || toKebab(post.title);
      if (slug) setUtmCampaign(slug);
      // Pre-fill base URL if the post has a published URL
      if (post.url) setBaseUrl(post.url);
    }
  }

  const campaignValid = isValidKebab(utmCampaign);
  const contentValid = !utmContent || isValidKebab(utmContent);
  const baseValid = (() => { try { new URL(baseUrl); return true; } catch { return false; } })();
  const formValid = baseValid && campaignValid && contentValid;

  const generatedUrl = useMemo(() => {
    if (!formValid) return '';
    return buildUtmUrl({ baseUrl, utmSource, utmMedium, utmCampaign, utmContent, utmTerm });
  }, [baseUrl, utmSource, utmMedium, utmCampaign, utmContent, utmTerm, formValid]);

  async function handleCopy() {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  }

  async function handleSaveAndCopy() {
    if (!formValid) { setErr('Fix validation errors before saving'); return; }
    setSaving(true);
    setErr(null);
    try {
      // Save to history
      const res = await apiFetch('/api/data/utm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseUrl, utmSource, utmMedium, utmCampaign,
          utmContent: utmContent || null,
          utmTerm: utmTerm || null,
          generatedUrl,
          contentLogId: contentLogId ? Number(contentLogId) : null,
          notes: notes || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      // Prepend to local history
      setHistory(prev => [data.utm, ...prev]);
      // Copy to clipboard
      await handleCopy();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Convention reference */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#888] mb-3">BWM UTM Convention v2 (GA4-aligned)</div>
        <div className="text-[13px] text-[#333] leading-relaxed space-y-2">
          <div><span className="font-mono text-[11px] bg-bone/60 px-1.5 py-0.5 rounded">utm_source</span> — where the click came from. Allowed: <span className="font-mono text-[11px]">{UTM_SOURCES.map(s => s.id).join(' | ')}</span></div>
          <div><span className="font-mono text-[11px] bg-bone/60 px-1.5 py-0.5 rounded">utm_medium</span> — channel category (feeds GA4 Default Channel Group). Allowed: <span className="font-mono text-[11px]">{UTM_MEDIUMS.map(m => m.id).join(' | ')}</span></div>
          <div><span className="font-mono text-[11px] bg-bone/60 px-1.5 py-0.5 rounded">utm_campaign</span> — kebab-case identifier (post slug or campaign name). Lowercase letters, digits, hyphens.</div>
          <div><span className="font-mono text-[11px] bg-bone/60 px-1.5 py-0.5 rounded">utm_content</span> — optional, the specific asset/variant.</div>
          <div><span className="font-mono text-[11px] bg-bone/60 px-1.5 py-0.5 rounded">utm_term</span> — optional, placement on the page. Allowed: <span className="font-mono text-[11px]">{UTM_TERMS.filter(t => t.id).map(t => t.id).join(' | ')}</span></div>
        </div>
      </div>

      {/* Builder */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#888] mb-3">Build a UTM Link</div>

        {/* Pick-from-existing-post */}
        <div className="mb-4">
          <label className={labelClass}>Pick from existing post (optional — auto-fills campaign + URL)</label>
          <select value={contentLogId} onChange={e => handlePickPost(e.target.value)} className={inputClass}>
            <option value="">— No post selected —</option>
            {posts.map(p => (
              <option key={p.id} value={p.id}>
                {p.title}{p.slug ? ` — ${p.slug}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12">
            <label className={labelClass}>Base URL</label>
            <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} className={inputClass + ' font-mono text-[12px]'} />
            {!baseValid && <div className="text-[11px] text-rose mt-1">Not a valid URL</div>}
          </div>

          <div className="col-span-6">
            <label className={labelClass}>utm_source</label>
            <select value={utmSource} onChange={e => setUtmSource(e.target.value)} className={inputClass}>
              {UTM_SOURCES.map(s => <option key={s.id} value={s.id}>{s.label} ({s.id})</option>)}
            </select>
          </div>

          <div className="col-span-6">
            <label className={labelClass}>utm_medium</label>
            <select value={utmMedium} onChange={e => setUtmMedium(e.target.value)} className={inputClass}>
              {UTM_MEDIUMS.map(m => <option key={m.id} value={m.id}>{m.label} ({m.id})</option>)}
            </select>
          </div>

          <div className="col-span-6">
            <label className={labelClass}>utm_campaign (kebab-case)</label>
            <input
              value={utmCampaign}
              onChange={e => setUtmCampaign(e.target.value)}
              onBlur={e => setUtmCampaign(toKebab(e.target.value))}
              placeholder="tax-planning-vs-tax-preparation"
              className={inputClass + ' font-mono text-[12px]'}
            />
            {utmCampaign && !campaignValid && <div className="text-[11px] text-rose mt-1">Must be lowercase letters, digits, hyphens. No spaces or special chars.</div>}
          </div>

          <div className="col-span-6">
            <label className={labelClass}>utm_content (optional — specific asset)</label>
            <input
              value={utmContent}
              onChange={e => setUtmContent(e.target.value)}
              onBlur={e => setUtmContent(toKebab(e.target.value))}
              placeholder="medical-practice"
              className={inputClass + ' font-mono text-[12px]'}
            />
            {utmContent && !contentValid && <div className="text-[11px] text-rose mt-1">Same kebab-case rule.</div>}
          </div>

          <div className="col-span-6">
            <label className={labelClass}>utm_term (optional — placement on page)</label>
            <select value={utmTerm} onChange={e => setUtmTerm(e.target.value)} className={inputClass}>
              {UTM_TERMS.map(t => <option key={t.id || 'none'} value={t.id}>{t.label}{t.id ? ` (${t.id})` : ''}</option>)}
            </select>
          </div>

          <div className="col-span-12">
            <label className={labelClass}>Notes (optional, for history)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} className={inputClass} placeholder="Where this link was placed, audience, etc." />
          </div>
        </div>

        {/* Generated output */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-1">
            <label className={labelClass}>Generated URL</label>
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                disabled={!generatedUrl}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-[3px] ${
                  copied ? 'bg-deep-teal/15 text-deep-teal' :
                  generatedUrl ? 'bg-bullogic-blue/10 text-bullogic-blue hover:bg-bullogic-blue/20' :
                  'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handleSaveAndCopy}
                disabled={!formValid || saving}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-[3px] ${
                  saving ? 'bg-gray-200 text-gray-400' :
                  formValid ? 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90' :
                  'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving...' : 'Save + Copy'}
              </button>
            </div>
          </div>
          <textarea
            readOnly
            value={generatedUrl || (formValid ? '' : 'Fill in required fields to generate URL')}
            rows={2}
            className={inputClass + ' font-mono text-[11px] bg-bone/30'}
            onFocus={e => e.target.select()}
          />
          {err && <div className="text-[11px] text-rose mt-1">{err}</div>}
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#888] mb-3">History (most recent {history.length})</div>
        {loadingHistory ? (
          <div className="text-[12px] text-[#888]">Loading...</div>
        ) : history.length === 0 ? (
          <div className="text-[12px] text-[#888] italic">No links saved yet. Build one above and click Save + Copy.</div>
        ) : (
          <div className="space-y-2">
            {history.map(h => (
              <UtmHistoryRow
                key={h.id}
                entry={h}
                onDelete={async (id) => {
                  if (!window.confirm('Delete this UTM link from history? This cannot be undone.')) return;
                  try {
                    const res = await apiFetch(`/api/data/utm/${id}`, { method: 'DELETE' });
                    if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Delete failed'); }
                    setHistory(prev => prev.filter(x => x.id !== id));
                  } catch (e) {
                    setErr(e.message);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UtmHistoryRow({ entry, onDelete }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(entry.generatedUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  }
  const when = entry.createdAt ? new Date(entry.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  return (
    <div className="bg-bone/30 rounded-[3px] px-3 py-2 text-[12px]">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono text-[11px] bg-bullogic-blue/10 text-bullogic-blue px-1.5 py-0.5 rounded">{entry.utmSource}</span>
        <span className="font-mono text-[11px] bg-deep-teal/10 text-deep-teal px-1.5 py-0.5 rounded">{entry.utmMedium}</span>
        <span className="font-bold text-navy">{entry.utmCampaign}</span>
        {entry.utmContent && <span className="text-[#888] font-mono text-[11px]">/ {entry.utmContent}</span>}
        {entry.utmTerm && <span className="text-[#888] font-mono text-[11px]">@ {entry.utmTerm}</span>}
        <span className="flex-1" />
        <span className="text-[#888] text-[10px]">{when}</span>
        <span className="text-[#888] text-[10px]">{entry.createdBy || ''}</span>
        <button onClick={copy} className={`text-[10px] font-bold px-2 py-0.5 rounded-[3px] ${copied ? 'bg-deep-teal/15 text-deep-teal' : 'bg-bullogic-blue/10 text-bullogic-blue hover:bg-bullogic-blue/20'}`}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
        {onDelete && (
          <button
            onClick={() => onDelete(entry.id)}
            className="text-[10px] font-bold px-2 py-0.5 rounded-[3px] bg-rose/10 text-rose hover:bg-rose/20"
            title="Delete this UTM from history"
          >
            Delete
          </button>
        )}
      </div>
      <div className="font-mono text-[10px] text-[#666] truncate" title={entry.generatedUrl}>{entry.generatedUrl}</div>
      {entry.notes && <div className="text-[10px] text-[#888] italic mt-1">{entry.notes}</div>}
    </div>
  );
}
